import { Step } from "react-joyride";

const waitFor = (selector: string, timeoutMs = 2000): Promise<void> =>
  new Promise((resolve) => {
    if (document.querySelector(selector)) {
      resolve();
      return;
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        observer.disconnect();
        resolve();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      resolve();
    }, timeoutMs);
  });

const openFirstJobCard = async () => {
  if (document.querySelector(".tour-project-details-list")) return;
  (document.querySelector(".tour-job-card") as HTMLElement | null)?.click();
  await waitFor(".tour-project-details-list");
};

const switchToMapView = async () => {
  if (document.querySelector(".tour-map-view")) return;
  (
    document.querySelector('.tour-schedule-view-toggle [aria-label="Map view"]') as HTMLElement | null
  )?.click();
  await waitFor(".tour-map-view");
};

const switchToListView = () => {
  (document.querySelector('[aria-label="List view"]') as HTMLElement | null)?.click();
};

const showStatusLegend = async () => {
  const el = document.querySelector(".tour-status-legend") as HTMLElement | null;
  if (el) el.style.display = "flex";
};

const hideStatusLegend = () => {
  const el = document.querySelector(".tour-status-legend") as HTMLElement | null;
  if (el) el.style.display = "none";
};

const closePhaseModal = async () => {
  (document.querySelector(".tour-modal-close") as HTMLElement | null)?.click();
};

const openPhaseModal = async () => {
  if (document.querySelector(".tour-assign-to")) return;
  (document.querySelector(".tour-job-phase-row") as HTMLElement | null)?.click();
  await waitFor(".tour-assign-to");
};

const jobsSchedulePageTourSteps: Step[] = [
  {
    target: ".fc-header-toolbar",
    content: "Toggle through your calendar to see different dates.",//
  },
  {
    target: ".fc-viewMenu-button",
    content: "View the Calendar by Day, Month, Week, or specific Crew.",//
  },
  {
    target: ".fc-dispatch-day-btn",
    content:
      "View your Daily Schedule Readiness. This includes checklist completion, individual job readiness, material needs, and trucking demands.",
  },//
  {
    target: ".tour-schedule-view-toggle",
    content: "View your schedule as a List or a Map.",//
  },
  {
    target: ".tour-jobs-search",
    content: "Search through your Jobs by name or ID.",//
    placement: "right",
    skipScroll: true,
  },
  {
    target: ".tour-jobs-filter",
    content:
      "You can also filter by Job Status, Product Type, or City to narrow the list of applicable jobs.",
    placement: "right",
    skipScroll: true,
  },
  {
    target: ".tour-jobs-list",
    content:
      "This lists each job with its associated Job ID. Jobs can be dragged and dropped on to the calendar to schedule.",
    placement: "right",
    skipScroll: true,
    overlayClickAction: false,
    dismissKeyAction: false,
  },
  {
    target: ".tour-status-tbs",
    content: "Indicates a job needs To Be Scheduled.",
    placement: "right",
    skipScroll: true,
    before: showStatusLegend,
  },
  {
    target: ".tour-status-s",
    content: "Indicates a job has been scheduled.",
    placement: "right",
    skipScroll: true,
  },
  {
    target: ".tour-status-jc",
    content: "Indicates a job is currently in Job Costing.",
    placement: "right",
    skipScroll: true,
  },
  {
    target: ".tour-status-rb",
    content: "Indicates a job is Ready to Bill.",
    placement: "right",
    skipScroll: true,
    after: hideStatusLegend,
  },
  {
    target: ".tour-job-expand-arrow",
    content: "Click on a Job to expand the section and see each Phase of that Job.",
    placement: "right",
    skipScroll: true,
  },
  {
    target: ".tour-job-phase-row",
    content:
      "Each Job Phase can be individually opened for more information and/or individually dragged and dropped to schedule.",
    placement: "right",
    skipScroll: true,
    before: openFirstJobCard,
  },
  {
    target: ".tour-project-details-list",
    content:
      "Click on any Phases in the list or schedule to view the Job’s Work Order, input Job Cost, or unschedule.",
    before: async () => {
      await closePhaseModal();
      await openFirstJobCard();
    },
    skipScroll: true,
  },
  {
    target: ".tour-assign-to",
    content: "Choose to assign each Job Phase to an Individual or Crew",
    before: openPhaseModal,
  },
  {
    target: ".tour-select-crew",
    content: "Choose which Individual/s or Crew to assign the Job Phase.",
    before: openPhaseModal,
  },
  {
    target: ".tour-phase-start-date",
    content:
      "Choose a date and time to schedule the Job Phase. The selected Date and Time will show here.",
    before: openPhaseModal,
  },
  {
    target: ".tour-phase-hours",
    content: "Shows the expected time on Job. Simply type a number to make adjustments.",
    before: openPhaseModal,
  },
  {
    target: ".tour-phase-block-buttons",
    content: "You can add or remove days scheduled for this job phase.",
    before: openPhaseModal,
  },
  {
    target: ".tour-checklist-select",
    content: (
      <>
        Checklist Items help track what needs to be coordinated before or after a Job Phase is
        scheduled. Click to add items such as <strong>“Confirmed with Client”</strong> or{" "}
        <strong>“Needs Permits.”</strong>
      </>
    ),
    before: openPhaseModal,
  },
  {
    target: ".tour-add-event",
    content:
      "Add a custom Event to the schedule that can be assigned and scheduled similar to a Job Phase.",
    before: closePhaseModal,
  },
  /*
  {
    target: ".tour-map-view",
    content: "Zoom in to display jobs. Job markers can be dragged and dropped to the calendar to be scheduled.",
    before: switchToMapView,
    placement: "right",
  },
  {
    target: ".tour-map-filter",
    content: "Click here to filter by job status or product.",
  },
  {
    target: ".tour-map-crew-pm-filters",
    content: "Filter by Crew or Project Manager.",
    after: switchToListView,
  },*/
];

export default jobsSchedulePageTourSteps;
